import { Shield, Target } from "lucide-react";
import { usePublicData } from "../../hooks/usePublicData";

export default function PublicTeamStats() {
  const { data, loading } = usePublicData();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!data) return null;

  const { teams, standings } = data;

  const maxGoals = Math.max(...standings.map(s => Math.max(s.goals_for, s.goals_against)), 1);

  return (
    <div className="space-y-8">
      <div>
        <h1 className="font-heading text-3xl md:text-4xl font-extrabold uppercase tracking-tight">
          Team Stats
        </h1>
        <p className="text-muted-foreground mt-1">
          Team performance comparison
        </p>
      </div>

      <div className="grid gap-6">
        {standings.map((team, index) => {
          const teamData = teams.find(t => t.id === team.team_id);
          return (
            <div key={team.team_id} className="bg-card border border-border rounded-xl p-6">
              <div className="flex items-center gap-4 mb-4">
                <span className={`w-8 h-8 rounded-lg flex items-center justify-center font-bold ${
                  index === 0 ? "bg-amber-500/20 text-amber-500" :
                  index < 4 ? "bg-primary/20 text-primary" :
                  "bg-secondary text-muted-foreground"
                }`}>
                  {index + 1}
                </span>
                {teamData?.logo ? (
                  <img src={teamData.logo} alt="" className="w-12 h-12 rounded-lg object-cover" />
                ) : (
                  <div className="w-12 h-12 bg-secondary rounded-lg flex items-center justify-center">
                    <Shield className="w-6 h-6 text-muted-foreground" />
                  </div>
                )}
                <div className="flex-1">
                  <h3 className="font-heading text-lg font-bold">{team.team_name}</h3>
                  <p className="text-sm text-muted-foreground">
                    {team.played} played • {team.won}W {team.drawn}D {team.lost}L
                  </p>
                </div>
                <div className="text-right">
                  <div className="font-heading text-3xl font-black text-primary">{team.points}</div>
                  <div className="text-xs text-muted-foreground">POINTS</div>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                <div className="bg-background rounded-lg p-3 text-center">
                  <div className="font-bold text-green-500">{team.won}</div>
                  <div className="text-xs text-muted-foreground">Wins</div>
                </div>
                <div className="bg-background rounded-lg p-3 text-center">
                  <div className="font-bold">{team.drawn}</div>
                  <div className="text-xs text-muted-foreground">Draws</div>
                </div>
                <div className="bg-background rounded-lg p-3 text-center">
                  <div className="font-bold text-red-500">{team.lost}</div>
                  <div className="text-xs text-muted-foreground">Losses</div>
                </div>
                <div className="bg-background rounded-lg p-3 text-center">
                  <div className={`font-bold ${team.goal_difference > 0 ? "text-green-500" : team.goal_difference < 0 ? "text-red-500" : ""}`}>
                    {team.goal_difference > 0 ? "+" : ""}{team.goal_difference}
                  </div>
                  <div className="text-xs text-muted-foreground">GD</div>
                </div>
              </div>

              <div className="space-y-3">
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-muted-foreground">Goals Scored</span>
                    <span className="font-bold text-green-500">{team.goals_for}</span>
                  </div>
                  <div className="h-2 bg-secondary rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-green-500 rounded-full transition-all"
                      style={{ width: `${(team.goals_for / maxGoals) * 100}%` }}
                    />
                  </div>
                </div>
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-muted-foreground">Goals Conceded</span>
                    <span className="font-bold text-red-500">{team.goals_against}</span>
                  </div>
                  <div className="h-2 bg-secondary rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-red-500 rounded-full transition-all"
                      style={{ width: `${(team.goals_against / maxGoals) * 100}%` }}
                    />
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
